import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Facebook, Instagram, Youtube, Twitter } from 'lucide-react';
import { LanguageContext } from '../App';
import TrustpilotWidget from './TrustpilotWidget';
import { normalizeTrustpilotDomain, trustpilotReviewUrl } from '../utils/trustpilotUtils';

const footerLinkClass =
  'block py-1.5 text-sm text-white/60 hover:text-green-400 transition leading-snug break-words';

const Footer = ({ settings }) => {
  const { t, language } = useContext(LanguageContext);
  const trustpilotDomain = normalizeTrustpilotDomain(settings?.trustpilot_business_id);
  const trustpilotUnitId = (settings?.trustpilot_business_unit_id || '').trim();
  const showTrustpilot = Boolean(settings?.trustpilot_enabled && trustpilotDomain);

  const links = settings?.social_links || {};
  const socialItems = [
    { key: 'facebook', label: 'Facebook', href: links.facebook, Icon: Facebook },
    { key: 'instagram', label: 'Instagram', href: links.instagram, Icon: Instagram },
    { key: 'tiktok', label: 'TikTok', href: links.tiktok, Icon: null },
    { key: 'youtube', label: 'YouTube', href: links.youtube, Icon: Youtube },
    { key: 'twitter', label: 'Twitter', href: links.twitter, Icon: Twitter },
    { key: 'telegram', label: 'Telegram', href: links.telegram, Icon: null },
    { key: 'whatsapp', label: 'WhatsApp', href: links.whatsapp, Icon: MessageCircle },
  ].filter((i) => i.href && String(i.href).trim());

  const companyLinks = useMemo(
    () => [
      { to: '/', label: t('aboutUs') },
      { to: '/products', label: t('products') },
      { to: '/', label: t('contact') },
      { to: '/blog', label: t('footerBlogs') },
      { to: '/how-it-works', label: t('howItWorksLink') },
    ],
    [t],
  );

  const legalLinks = useMemo(
    () => [
      { to: '/terms', label: t('termsConditions') },
      { to: '/privacy-policy', label: t('privacyPolicy') },
      { to: '/refund-policy', label: t('refundPolicy') },
      { to: '/aml-policy', label: t('amlPolicy') },
      { to: '/help', label: t('helpCenter') },
    ],
    [t],
  );

  const categoryLinks = useMemo(
    () => [
      { to: '/products/giftcard', label: t('giftCards') },
      { to: '/products/topup', label: t('topupHeroTitle') },
      { to: '/products/subscription', label: t('subscriptions') },
      { to: '/products/service', label: t('services') },
    ],
    [t],
  );

  const siteName = settings?.site_name || 'KayiCom';

  return (
    <footer className="mt-12 border-t border-white/5 bg-[#0a0a0a] overflow-x-hidden">
      <div className="container mx-auto max-w-6xl px-4 py-8 sm:py-10">
        <div className="mb-8">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Logo" className="h-9 w-auto mb-3 sm:h-10" />
          ) : (
            <img src="/images/kayeecomlogo.png" alt="KayiCom" className="h-9 w-auto mb-3 sm:h-10" />
          )}
          <p className="text-sm text-white/50 leading-relaxed max-w-md">{t('footerTagline')}</p>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-2 md:grid-cols-4 md:gap-8">
          <div>
            <h3 className="text-white font-semibold text-sm mb-3">{t('footerCompany')}</h3>
            <ul>
              {companyLinks.map(({ to, label }) => (
                <li key={`${to}-${label}`}>
                  <Link to={to} className={footerLinkClass}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-sm mb-3">{t('legalPagesNav')}</h3>
            <ul>
              {legalLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={footerLinkClass}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-sm mb-3">{t('footerCategories')}</h3>
            <ul>
              {categoryLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={footerLinkClass}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-sm mb-3">{t('footerSocial')}</h3>
            <ul>
              {socialItems.map(({ key, label, href }) => (
                <li key={key}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={footerLinkClass}
                  >
                    {label}
                  </a>
                </li>
              ))}
              {socialItems.length === 0 && (
                <li className="text-sm text-white/40 py-1">{t('footerSocialComingSoon')}</li>
              )}
            </ul>
          </div>
        </div>

        {showTrustpilot && (
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col items-center gap-4">
            {trustpilotUnitId ? (
              <div className="w-full max-w-md min-h-[28px] flex justify-center overflow-hidden">
                <TrustpilotWidget
                  businessUnitId={trustpilotUnitId}
                  reviewDomain={trustpilotDomain}
                  height="28px"
                />
              </div>
            ) : null}
            <a
              href={trustpilotReviewUrl(trustpilotDomain, language)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-green-500 text-black font-semibold rounded-lg hover:bg-green-600 transition text-sm"
            >
              {t('trustpilotViewReviews')}
            </a>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-xs sm:text-sm text-white/40 leading-relaxed">
            &copy;{new Date().getFullYear()} {siteName}. {t('allRightsReserved')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
